import React from 'react';

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'outline' | 'ghost';
  size?: 'sm' | 'md' | 'lg';
  asChild?: boolean;
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    { className = '', variant = 'primary', size = 'md', asChild = false, children, ...props },
    ref,
  ) => {
    const baseStyles =
      'inline-flex items-center justify-center gap-2 font-bold cursor-pointer transition-all active:scale-95 disabled:opacity-50 disabled:pointer-events-none';

    const variants = {
      primary: 'bg-primary text-on-primary shadow-lg shadow-primary/10 hover:bg-primary-dim',
      outline:
        'border border-outline-variant/30 text-on-surface-variant hover:bg-primary/5 hover:text-primary',
      ghost: 'text-on-surface-variant hover:bg-surface-container-low hover:text-on-surface',
    };

    const sizes = {
      sm: 'px-4 py-1.5 text-xs rounded-full uppercase tracking-wider',
      md: 'px-6 py-2.5 text-sm rounded-xl',
      lg: 'px-6 py-3 text-base rounded-xl',
    };

    const combinedClasses = `${baseStyles} ${variants[variant]} ${sizes[size]} ${className}`;

    if (asChild) {
      // Very basic generic unwrapper if asChild is passed (expects a single valid element child like <Link> or <label>)
      if (React.isValidElement(children)) {
        const childProps = children.props as { className?: string };
        return React.cloneElement(children as React.ReactElement<{ className?: string }>, {
          className: `${combinedClasses} ${childProps.className || ''}`.trim(),
          ...props,
        });
      }
    }

    return (
      <button ref={ref} className={combinedClasses} {...props}>
        {children}
      </button>
    );
  },
);
Button.displayName = 'Button';
